import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import styles from '../style.module.scss';
import { FlyoutType } from '../Utils/utils';
import { useTranslation } from 'react-i18next';
import { Loan, LoanStatusResponse, PortfolioInsuranceType } from '../types';
import { ForceStatusPill } from '../ForceStatusPill';
import { Button, ButtonVariant, CmhcIcon, CmhcIconSize, Divider } from '@cmhc-schl/hearth-ui';
import { Remark } from '../../../types';

interface SuccessResultInterface {
  pptCode: string | null;
  typeOfFlyout?: FlyoutType;
  successFlyoutDesc?: string;
  successFlyoutTitle?: string;
  loansTypeToBeAdded?: string;
  copiedButtonText?: string | VariableText;
  copyButtonText?: string | VariableText;
  successfulLoans?: LoanStatusResponse[];
  typeOfPortfolioInsurance: PortfolioInsuranceType;
  flyoutLoans: Loan[];
  remark?: Remark;
}

interface VariableText {
  singular: string;
  plural: string;
}

export const getVariableText = (count: number, text?: string | VariableText) => {
  if (!text) return undefined;

  if (typeof text === 'string') {
    return text;
  }
  return count > 1 ? text.plural : text.singular;
};

export const SuccessResult = ({
  pptCode,
  typeOfFlyout,
  successFlyoutDesc,
  successFlyoutTitle,
  successfulLoans,
  loansTypeToBeAdded,
  typeOfPortfolioInsurance,
  flyoutLoans,
  copiedButtonText,
  copyButtonText,
  remark
}: SuccessResultInterface) => {
  const { t } = useTranslation('HbtMasterPool');
  const [copied, setCopied] = useState(false);
  const copiedKeyBase = 'Loan-Numbers-Copied-Button-Text';
  const copyKeyBase = 'Copy-Loan-Numbers-Button-Text';
  const timerRef = useRef<number | null>(null);

  const copiedKey =
    successfulLoans && successfulLoans.length === 1
      ? `${copiedKeyBase}-Singular`
      : `${copiedKeyBase}-Plural`;
  const copyKey =
    successfulLoans && successfulLoans.length === 1
      ? `${copyKeyBase}-Singular`
      : `${copyKeyBase}-Plural`;

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  const handleCopy = async () => {
    const text =
      successfulLoans && successfulLoans.map((loan) => loan.cmhcAccountNumber).join('\n');
    await navigator.clipboard.writeText(text ?? '');
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 5000);
  };
  return (
    <Box className={`${styles.flyoutWrapper} ${styles.flyoutDesc}`}>
      <Typography className={styles.flyout} variant="h6">
        {(pptCode && typeOfFlyout === 'updateLoan' && successFlyoutDesc) ||
          t('Status-Update-Success-Desc')}
      </Typography>
      <Typography className={styles.sectionTitle}>
        {(pptCode && typeOfFlyout === 'updateLoan' && successFlyoutTitle) || t('Loans-Updated')}
      </Typography>

      {successfulLoans && successfulLoans.length > 0 && (
        <TableContainer component={Paper} className={styles.loansTableContainer} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow className={styles.loansTableHeader}>
                <TableCell className={styles.loansTableHeaderCell}>
                  {loansTypeToBeAdded ?? t('CMHC-Account-Number')}
                </TableCell>
                <TableCell className={styles.loansTableHeaderCell}>{t('Status')}</TableCell>
                <TableCell
                  className={`${styles.loansTableHeaderCell} ${styles.loansTableHeaderCellActions}`}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {successfulLoans.map((loan) => (
                <TableRow key={loan.cmhcAccountNumber}>
                  <TableCell className={styles.loansTableCell}>
                    <Box className={styles.loansAccountNumber}>
                      {loan.cmhcAccountNumber ?? loan.lenderReferenceNumber ?? ''}
                    </Box>
                  </TableCell>
                  <TableCell className={`${styles.loansTableCell} ${styles.loanStatusPill}`}>
                    <ForceStatusPill
                      typeOfPortfolioInsurance={typeOfPortfolioInsurance}
                      status={loan.status ?? loan.portfolioLoanAccountStatus ?? ''}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {successfulLoans && successfulLoans.length > 0 && (
        <>
          <Box className={styles.addedLoansCount}>
            <Box>
              {t(flyoutLoans.length === 1 ? 'ItemsSingular' : 'ItemsPlural', {
                count: flyoutLoans.length
              })}
            </Box>
          </Box>
          <Divider
            screenReaderText="next Section"
            spacingType="variable"
            variableMargins={[0, 0]}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2
            }}
          >
            <Button
              customStyles={{ textDecoration: 'none' }}
              name="copyButton"
              text={
                copied
                  ? (getVariableText(successfulLoans.length, copiedButtonText) ?? t(copiedKey))
                  : (getVariableText(successfulLoans.length, copyButtonText) ?? t(copyKey))
              }
              ariaText={{
                value: copied
                  ? (getVariableText(successfulLoans.length, copiedButtonText) ?? t(copiedKey))
                  : (getVariableText(successfulLoans.length, copyButtonText) ?? t(copyKey))
              }}
              variant={ButtonVariant.TERTIARY}
              onClick={handleCopy}
              leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_copy" />}
            />
          </Box>
          <Box className={styles.remarksSuccess}>
            <Typography className={styles.sectionTitle}>{t('Remarks-Added')}</Typography>
            <Typography className={styles.remarksValue}>{remark?.remarkMessage ?? ''}</Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
